/* eslint-disable no-unused-expressions */
import React, {
  useRef,
  useMemo,
  useCallback,
  useState,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { Field, useField, useForm } from 'react-final-form';
import { OnChange } from 'react-final-form-listeners';
import {
  Col,
  Label,
  Row,
  TextField,
  IconButton,
  Icon,
  Callout,
  Tooltip,
} from '@folio/stripes-components';
import { useIntl, FormattedMessage } from 'react-intl';
import { Pluggable, CalloutContext } from '@folio/stripes/core';
import {
  required,
  composeValidators,
  authorityMinLength,
  authorityTextOnly,
} from '../../../utils/utils';
import { getAuthorityTypeForField } from '../../../constants';
import { AuthoritySuggestions } from '../../Authority';
import {
  isFieldEnabledForAuthority,
  getAuthoritySettings,
  getAuthoritySourceForField,
  isAutoCreateAuthorityEnabled,
  isAutoLinkingEnabled,
} from '../../../utils/configUtils';
import { useFieldAutoLinking } from '../../Authority/hooks/useFieldAutoLinking';
import css from '../styles.css';
import HelpeModal from '../../HelpeModal';

const propTypes = {
  fieldValues: PropTypes.object,
  index: PropTypes.number,
  fieldName: PropTypes.string,
  parentField: PropTypes.object,
  showHelpeIcon: PropTypes.bool,
  controlledVocab: PropTypes.object,
  actionType: PropTypes.string, // 'create' or 'edit'
};

function TextFieldComponent({
  fieldValues,
  index,
  fieldName,
  parentField,
  showHelpeIcon,
  controlledVocab,
  actionType = 'create',
}) {
  const { locale } = useIntl();
  const form = useForm();
  const callout = useRef();
  const authorityFieldRef = useRef();
  const [showSuggestions, setShowSuggestions] = useState(false);
  const initialFormData = useRef(null); // Store initial form data when opening for edit
  // console.log({ controlledVocab });
  const { name, label, type, isRequired, canEdit, isRepeatable, helpeText } =
    fieldValues;
  const fieldLabel = label.find((field) => field.locale === locale)?.value;

  const fieldPath = useMemo(() => {
    return parentField?.type === 'fieldArray'
      ? `${parentField.name}.${name}`
      : name;
  }, [parentField, name]);

  // Store initial form data immediately when in edit mode
  if (actionType === 'edit' && !initialFormData.current) {
    const formValues = form.getState().values;
    initialFormData.current = formValues;
  }

  // Get authority settings from controlledVocab
  const authoritySettings = getAuthoritySettings(controlledVocab);
  const isAuthorityField = isFieldEnabledForAuthority(
    fieldPath,
    controlledVocab,
  );

  const authoritySource = getAuthoritySourceForField(
    fieldPath,
    controlledVocab,
  );
  const isAutoCreateEnabled = isAutoCreateAuthorityEnabled(controlledVocab);
  const isAutoLinkingEnabledSetting = isAutoLinkingEnabled(controlledVocab);

  const authorityField = useField(
    parentField?.name ? `instanceData.${parentField.name}[${index}]` : 'dummy',
    { subscription: { value: true } },
  );

  const textValue = useMemo(() => {
    return authorityField.input.value?.[name] || '';
  }, [authorityField.input.value, name]);

  // Field auto-linking hook for edit mode
  const { processCurrentField, isProcessing } = useFieldAutoLinking({
    fieldPath,
    textValue,
    index,
    formValues: form.getState().values,
    controlledVocab,
    onLinkAuthority: (fieldName, arrayIndex, propName, authority) => {
      // Apply authority linking to form
      form.change(
        `instanceData.${fieldName}[${arrayIndex}].${propName}`,
        authority.headingRef,
      );
      form.change(
        `instanceData.${fieldName}[${arrayIndex}].authorityId`,
        authority.id,
      );
    },
    onShowMessage: (type, messageId, values) => {
      callout.current?.sendCallout({
        type,
        message: (
          <FormattedMessage
            id={messageId}
            defaultMessage={
              messageId.includes('existing')
                ? 'Authority record found and linked automatically: {term}'
                : 'New authority record created and linked automatically: {term}'
            }
            values={values}
          />
        ),
      });
    },
  });

  const hasAuthority = useMemo(() => {
    return !!authorityField.input.value?.authorityId;
  }, [authorityField.input.value?.authorityId]);

  const contributorNameTypeId = useMemo(() => {
    return authorityField.input.value?.contributorNameTypeId;
  }, [authorityField.input.value?.contributorNameTypeId]);

  // Validation function for authority linking
  const validateAuthorityLink = (value) => {
    const currentAuthorityId = authorityField.input.value?.authorityId;

    if (!currentAuthorityId) {
      return (
        <FormattedMessage
          id="ui-plugin-inventory-templates.authority.validation.mustLink"
          defaultMessage="This term should be linked to an authority record"
        />
      );
    }

    return undefined;
  };

  const handleLinkAuthority = useCallback(
    (authority) => {
      if (!authority || !authority.headingRef || !authority.id) {
        callout.current?.sendCallout({
          type: 'error',
          message: 'Invalid authority record: missing required fields',
        });
        return;
      }

      form.change(
        `instanceData.${parentField.name}[${index}].${name}`,
        authority.headingRef,
      );
      form.change(
        `instanceData.${parentField.name}[${index}].authorityId`,
        authority.id,
      );

      callout.current?.sendCallout({
        type: 'success',
        message: (
          <FormattedMessage
            id="ui-plugin-inventory-templates.authority.linking.success"
            defaultMessage="Authority record linked successfully"
          />
        ),
      });
    },
    [form, parentField?.name, index, name],
  );

  const handleFieldFocus = useCallback(() => {
    // Check if text is valid (not purely numeric)
    const isValidText = authorityTextOnly(textValue) === undefined; // If no error returned = valid text
    if (!hasAuthority && textValue && textValue.length >= 3 && isValidText) {
      setShowSuggestions(true);
    }
  }, [hasAuthority, textValue]);

  const handleFieldValueChange = useCallback(
    (value) => {
      // Check if text is valid (not purely numeric)
      const isValidText = authorityTextOnly(value) === undefined; // If no error returned = valid text
      if (!hasAuthority && value && value.length >= 3 && isValidText) {
        setShowSuggestions(true); // Only for valid text
      } else {
        setShowSuggestions(false);
        authorityFieldRef.current?.blur();
        authorityFieldRef.current?.focus();
      }
    },
    [hasAuthority],
  );

  const handleSelectSuggestion = useCallback(
    (suggestion) => {
      handleLinkAuthority(suggestion);
      setShowSuggestions(false);
    },
    [handleLinkAuthority],
  );

  const handleUnlinkAuthority = useCallback(() => {
    form.change(
      `instanceData.${parentField.name}[${index}].authorityId`,
      undefined,
    );

    callout.current?.sendCallout({
      type: 'success',
      message: (
        <FormattedMessage
          id="ui-plugin-inventory-templates.authority.unlinking.success"
          defaultMessage="Authority record unlinked successfully"
        />
      ),
    });
  }, [form, parentField?.name, index]);

  // Check if this field exists in initial form data
  const isInitialField = useMemo(() => {
    if (!initialFormData.current || !parentField?.name) {
      return false;
    }

    const initialFieldData =
      initialFormData.current?.instanceData?.[parentField.name]?.[index];

    return !!initialFieldData; // Field exists in initial data
  }, [parentField, index, fieldPath]);

  // Initial auto-linking effect for edit mode - only for initial fields
  useEffect(() => {
    if (
      actionType === 'edit' &&
      isAuthorityField &&
      isAutoLinkingEnabledSetting && // Check if auto-linking is enabled
      !isProcessing &&
      isInitialField && // Only process fields that existed in initial form data
      textValue && // Has text value
      !hasAuthority // Not already linked
    ) {
      processCurrentField();
    }
  }, []);

  const getAuthorityConfig = useCallback(() => {
    if (!isAuthorityField) return {};

    const formValues = form.getState().values;
    const arrayIndex =
      parentField?.type === 'fieldArray'
        ? parseInt(fieldName.split('.')[1].match(/\[(\d+)\]/)?.[1] || '0')
        : 0;

    return getAuthorityTypeForField(
      fieldPath,
      formValues,
      arrayIndex,
      controlledVocab,
    );
  }, [
    isAuthorityField,
    form,
    fieldPath,
    parentField,
    fieldName,
    controlledVocab,
  ]);

  const helpeCondition =
    showHelpeIcon &&
    parentField?.type !== 'fieldArray' &&
    !isRepeatable &&
    helpeText;
  const containerCondition =
    parentField?.type !== 'fieldArray' && !isRepeatable;

  const handleFieldLabelAppeerance = () => {
    if (isRepeatable || (parentField?.type === 'fieldArray' && index !== 0)) {
      return null;
    }

    return <Label fullWidth>{fieldLabel}</Label>;
  };

  return (
    <CalloutContext.Provider value={callout}>
      <Row className={containerCondition ? css.container : ''}>
        <Col xs={helpeCondition ? 11 : 12}>
          <div style={{ position: 'relative' }}>
            <div
              style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}
            >
              <div style={{ flex: 1, position: 'relative' }}>
                <Field
                  inputRef={authorityFieldRef}
                  id={`inventory-templates-form-generator-${type}-field-${index}`}
                  component={TextField}
                  fullWidth
                  name={fieldName}
                  label={handleFieldLabelAppeerance()}
                  required={isRequired}
                  validate={composeValidators(
                    ...[
                      isRequired && required,
                      isAuthorityField && authorityMinLength,
                      isAuthorityField && authorityTextOnly,
                      isAuthorityField && validateAuthorityLink,
                    ].filter(Boolean),
                  )}
                  type="text"
                  disabled={
                    !canEdit ||
                    (hasAuthority && isAuthorityField) ||
                    isProcessing
                  }
                  parse={(value) => {
                    // Only convert empty strings to null for non-authority fields
                    if (!isAuthorityField && value === '') {
                      return null;
                    }
                    return value;
                  }}
                  onFocus={handleFieldFocus}
                  startControl={
                    isAuthorityField &&
                    (hasAuthority ? (
                      <Icon
                        icon="check-circle"
                        size="small"
                        iconClassName={css.authorityLinked}
                      />
                    ) : isProcessing ? (
                      <Icon
                        icon="spinner-ellipsis"
                        size="small"
                        iconClassName={css.autoLinkingSpinner}
                      />
                    ) : null)
                  }
                />

                <OnChange
                  name={
                    parentField?.type === 'fieldArray'
                      ? `instanceData.${parentField.name}[${index}].${name}`
                      : fieldName
                  }
                >
                  {handleFieldValueChange}
                </OnChange>

                {isAuthorityField && showSuggestions && (
                  <AuthoritySuggestions
                    fieldPath={fieldPath}
                    textValue={textValue}
                    isAuthorityField={isAuthorityField}
                    hasAuthority={hasAuthority}
                    formValues={form.getState().values}
                    arrayIndex={index}
                    controlledVocab={controlledVocab}
                    contributorNameTypeId={contributorNameTypeId}
                    authoritySettings={authoritySettings}
                    authoritySource={authoritySource}
                    isAutoCreateEnabled={isAutoCreateEnabled}
                    onSelectSuggestion={handleSelectSuggestion}
                    onHide={() => setShowSuggestions(false)}
                  />
                )}
              </div>

              {isAuthorityField && hasAuthority && (
                <div
                  style={{
                    marginTop: handleFieldLabelAppeerance() ? '24px' : '0',
                  }}
                >
                  <Tooltip
                    id="authority-unlink-tooltip"
                    text={
                      <FormattedMessage
                        id="ui-plugin-inventory-templates.authority.unlink.tooltip"
                        defaultMessage="Unlink authority record"
                      />
                    }
                  >
                    {({ ref, ariaIds }) => (
                      <IconButton
                        ref={ref}
                        onClick={handleUnlinkAuthority}
                        icon="unlink"
                        size="small"
                        style={{ minWidth: 'auto', padding: '6px' }}
                        data-testid="authority-unlink-button"
                        aria-labelledby={ariaIds.text}
                        ariaLabel={
                          <FormattedMessage
                            id="ui-plugin-inventory-templates.authority.unlink"
                            defaultMessage="Unlink authority record"
                          />
                        }
                      />
                    )}
                  </Tooltip>
                </div>
              )}

              {isAuthorityField && !hasAuthority && (
                <div
                  style={{
                    marginTop: handleFieldLabelAppeerance() ? '24px' : '0',
                  }}
                >
                  <Pluggable
                    type="find-authority"
                    onLinkRecord={handleLinkAuthority}
                    isLinkingLoading={false}
                    renderCustomTrigger={({ onClick }) => (
                      <Tooltip
                        id="authority-search-tooltip"
                        text={
                          <FormattedMessage
                            id="ui-plugin-inventory-templates.authority.search.advanced.tooltip"
                            defaultMessage="Advanced authority linking"
                          />
                        }
                      >
                        {({ ref, ariaIds }) => (
                          <IconButton
                            ref={ref}
                            onClick={onClick}
                            icon="search"
                            size="small"
                            style={{ minWidth: 'auto', padding: '6px' }}
                            data-testid="authority-advanced-search-button"
                            aria-labelledby={ariaIds.text}
                            ariaLabel={
                              <FormattedMessage
                                id="ui-plugin-inventory-templates.authority.search.advanced"
                                defaultMessage="Advanced authority search"
                              />
                            }
                          />
                        )}
                      </Tooltip>
                    )}
                    {...getAuthorityConfig()}
                  />
                </div>
              )}
            </div>
          </div>
        </Col>
        {helpeCondition && (
          <Col xs={1} className={css.margin_btn}>
            <HelpeModal
              fieldData={fieldValues}
              title={fieldLabel}
              iconSize="medium"
            />
          </Col>
        )}
      </Row>
      <Callout ref={callout} />
    </CalloutContext.Provider>
  );
}

TextFieldComponent.propTypes = propTypes;

export default TextFieldComponent;
